// Safe shim for useSyncExternalStoreWithSelector that works in all build environments
import React from "react";

type USSWS = <T, S>(
	subscribe: (onStoreChange: () => void) => () => void,
	getSnapshot: () => T,
	getServerSnapshot?: () => T,
	selector?: (state: T) => S,
	isEqual?: (a: S, b: S) => boolean,
) => S;

const {useRef, useEffect, useMemo, useDebugValue} = React;

// Get useSyncExternalStore function safely
function getUseSyncExternalStore(): any {
	// Try React 18+ built-in first
	if ((React as any).useSyncExternalStore) {
		return (React as any).useSyncExternalStore;
	}

	// Try to dynamically import the shim
	try {
		const shimModule = require("use-sync-external-store/shim");
		if (shimModule.useSyncExternalStore) {
			return shimModule.useSyncExternalStore;
		}
		if (typeof shimModule.default === "function") {
			return shimModule.default;
		}
		if (shimModule.default?.useSyncExternalStore) {
			return shimModule.default.useSyncExternalStore;
		}
	} catch (e) {
		console.warn("Failed to load use-sync-external-store/shim:", e);
	}

	// Fallback implementation for older React versions
	return function useSyncExternalStoreFallback<T>(
		subscribe: (onStoreChange: () => void) => () => void,
		getSnapshot: () => T,
		getServerSnapshot?: () => T,
	): T {
		const value = React.useSyncExternalStore
			? React.useSyncExternalStore(
					subscribe,
					getSnapshot,
					getServerSnapshot,
				)
			: useRef(getSnapshot()).current;

		React.useEffect(() => {
			return subscribe(() => {
				// Force re-render
				if (!React.useSyncExternalStore) {
					// This is a fallback - in real scenarios we'd need proper state management
					console.warn(
						"Using fallback useSyncExternalStore - this may not work correctly",
					);
				}
			});
		}, [subscribe]);

		return value;
	};
}

export const useSyncExternalStoreWithSelector: USSWS = (
	subscribe,
	getSnapshot,
	getServerSnapshot,
	selector = (state: any) => state,
	isEqual,
) => {
	const instRef = useRef<{hasValue: boolean; value: any} | null>(null);
	const inst = instRef.current ?? {hasValue: false, value: null};
	if (instRef.current === null) instRef.current = inst;

	const [getSelection, getServerSelection] = useMemo(() => {
		let hasMemo = false;
		let memoizedSnapshot: any;
		let memoizedSelection: any;

		const memoizedSelector = (nextSnapshot: any) => {
			if (!hasMemo) {
				hasMemo = true;
				memoizedSnapshot = nextSnapshot;
				const nextSelection = selector(nextSnapshot);
				if (isEqual !== undefined && inst.hasValue) {
					const currentSelection = inst.value;
					if (isEqual(currentSelection, nextSelection)) {
						memoizedSelection = currentSelection;
						return currentSelection;
					}
				}
				memoizedSelection = nextSelection;
				return nextSelection;
			}

			const prevSnapshot = memoizedSnapshot;
			const prevSelection = memoizedSelection;
			if (Object.is(prevSnapshot, nextSnapshot)) return prevSelection;

			const nextSelection = selector(nextSnapshot);
			if (
				isEqual !== undefined &&
				isEqual(prevSelection, nextSelection)
			) {
				return prevSelection;
			}
			memoizedSnapshot = nextSnapshot;
			memoizedSelection = nextSelection;
			return nextSelection;
		};

		const maybeGetServerSnapshot = getServerSnapshot ?? null;
		const getSnapshotWithSelector = () => memoizedSelector(getSnapshot());
		const getServerSnapshotWithSelector =
			maybeGetServerSnapshot === null
				? undefined
				: () => memoizedSelector(maybeGetServerSnapshot());

		return [
			getSnapshotWithSelector,
			getServerSnapshotWithSelector,
		] as const;
	}, [getSnapshot, getServerSnapshot, selector, isEqual]);

	const useSyncExternalStore = getUseSyncExternalStore();
	const value = useSyncExternalStore(
		subscribe,
		getSelection,
		getServerSelection,
	);

	useEffect(() => {
		inst.hasValue = true;
		inst.value = value;
	}, [value]);

	useDebugValue(value);
	return value as any;
};

export default {useSyncExternalStoreWithSelector};
